import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowUp, Copy, Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/Gate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CourseForm } from "@/pages/admin/CourseForm";
import { LessonForm } from "@/pages/admin/LessonForm";
import { STATUS_CLS } from "@/pages/admin/AdminCourses";
import { useMe } from "@/context/MeContext";
import { FORMAT_LABELS, errMsg, money, useApi } from "@/lib/api";

const IconBtn = ({ onClick, label, children, testid }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900" data-testid={testid}>{children}</button>
);

export default function AdminCourseEditor() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { me } = useMe();
  const can = (p) => me.user.permissions.includes(p);
  const [c, setC] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [newModule, setNewModule] = useState("");

  const load = useCallback(() => api.get(`/admin/courses/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e))), [api, id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); await load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = (status) => run(() => api.post(`/admin/courses/${id}/status`, { status }), `Course ${status}`);
  const review = (action) => {
    const comment = action === "request_changes" ? window.prompt("What needs to change?") : "";
    if (action === "request_changes" && !comment) return;
    run(() => api.post(`/admin/courses/${id}/review`, { action, comment }), action === "submit" ? "Sent for review" : action === "approve" ? "Approved and published" : "Changes requested");
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${c.title}" permanently? This cannot be undone.`)) return;
    try { await api.delete(`/admin/courses/${id}`); toast.success("Course deleted"); navigate("/admin/courses"); } catch (e) { toast.error(errMsg(e)); }
  };
  const duplicate = async () => {
    try { const { data } = await api.post(`/admin/courses/${id}/duplicate`); toast.success("Copy created"); navigate(`/admin/courses/${data.id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const addModule = () => run(async () => { await api.post(`/admin/courses/${id}/modules`, { title: newModule }); setNewModule(""); }, "Module added");

  const saveLesson = async (body) => {
    if (dialog.lesson) await api.put(`/admin/lessons/${dialog.lesson.id}`, body);
    else await api.post(`/admin/modules/${dialog.moduleId}/lessons`, body);
    toast.success("Lesson saved");
    setDialog(null);
    await load();
  };
  const saveCourse = async (body) => {
    await api.put(`/admin/courses/${id}`, body);
    toast.success("Course details saved");
    setDialog(null);
    await load();
  };
  const renameModule = (m) => {
    const title = window.prompt("Module title", m.title);
    if (title && title.trim().length >= 2) run(() => api.put(`/admin/modules/${m.id}`, { title: title.trim(), description: m.description || "" }), "Module renamed");
  };

  if (!c) return <Spinner />;
  return (
    <div>
      <Link to="/admin/courses" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#222d99]" data-testid="editor-back-btn"><ArrowLeft className="h-4 w-4" /> All courses</Link>
      <div className="mb-8 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Badge className={`capitalize ${STATUS_CLS[c.status]}`} data-testid="editor-course-status">{c.status.replace("_", " ")}</Badge>
          <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="editor-course-title">{c.title}</h1>
          {c.review_log?.length > 0 && (
            <ul className="mt-3 space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-slate-800" data-testid="editor-review-log">
              {c.review_log.slice(-3).reverse().map((r) => <li key={r.at}><span className="font-semibold">{r.action.replace("_", " ")}</span> by {r.by} · {new Date(r.at).toLocaleString()}{r.comment && <>: {r.comment}</>}</li>)}
            </ul>
          )}
          <p className="mt-1 text-sm text-slate-500">{c.level} · {c.duration_hours}h · {money(c.price, c.currency)} · {c.skills.join(", ")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setDialog({ kind: "course" })} data-testid="editor-edit-details-btn"><Pencil className="mr-1 h-4 w-4" /> Details</Button>
          <Button variant="outline" asChild data-testid="editor-preview-btn"><Link to={`/learn/courses/${c.id}`}><Eye className="mr-1 h-4 w-4" /> Preview</Link></Button>
          <Button variant="outline" onClick={duplicate} data-testid="editor-duplicate-btn"><Copy className="mr-1 h-4 w-4" /> Duplicate</Button>
          {c.status !== "published" && c.status !== "in_review" && can("courses.publish") && <Button onClick={() => setStatus("published")} className="bg-emerald-700 hover:bg-emerald-800" data-testid="editor-publish-btn">Publish</Button>}
          {c.status === "published" && can("courses.publish") && <Button variant="outline" onClick={() => setStatus("draft")} data-testid="editor-unpublish-btn">Move to draft</Button>}
          {c.status !== "archived" && can("courses.publish") && <Button variant="outline" onClick={() => setStatus("archived")} data-testid="editor-archive-btn">Archive</Button>}
          {c.status === "draft" && can("courses.edit") && <Button variant="outline" onClick={() => review("submit")} data-testid="editor-submit-review-btn">Send for review</Button>}
          {c.status === "in_review" && can("courses.publish") && <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => review("approve")} data-testid="editor-approve-btn">Approve & publish</Button>}
          {c.status === "in_review" && (can("courses.review") || can("courses.publish")) && <Button variant="outline" onClick={() => review("request_changes")} data-testid="editor-request-changes-btn">Request changes</Button>}
          {(c.status !== "published" ? can("courses.edit") : can("courses.delete")) && <Button variant="outline" className="border-rose-300 text-rose-700" onClick={remove} data-testid="editor-delete-btn">Delete</Button>}
        </div>
      </div>

      <div className="space-y-4" data-testid="editor-modules">
        {c.modules.map((m, mi) => (
          <div key={m.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`editor-module-${mi}`}>
            <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
              <p className="font-semibold text-slate-900"><span className="font-mono-label mr-2 text-xs text-slate-400">M{mi + 1}</span>{m.title}</p>
              <div className="flex">
                <IconBtn label="Rename" onClick={() => renameModule(m)} testid={`module-rename-${mi}`}><Pencil className="h-4 w-4" /></IconBtn>
                <IconBtn label="Move up" onClick={() => run(() => api.post(`/admin/modules/${m.id}/move`, { direction: "up" }))} testid={`module-up-${mi}`}><ArrowUp className="h-4 w-4" /></IconBtn>
                <IconBtn label="Move down" onClick={() => run(() => api.post(`/admin/modules/${m.id}/move`, { direction: "down" }))} testid={`module-down-${mi}`}><ArrowDown className="h-4 w-4" /></IconBtn>
                <IconBtn label="Delete module" onClick={() => window.confirm(`Delete "${m.title}" and its lessons?`) && run(() => api.delete(`/admin/modules/${m.id}`), "Module deleted")} testid={`module-delete-${mi}`}><Trash2 className="h-4 w-4" /></IconBtn>
              </div>
            </div>
            <ul>
              {m.lessons.map((l, li) => (
                <li key={l.id} className="flex items-center gap-3 border-b border-slate-100 px-4 py-2.5 last:border-0" data-testid={`editor-lesson-${mi}-${li}`}>
                  <Badge variant="outline" className="w-24 justify-center">{FORMAT_LABELS[l.type]}</Badge>
                  <span className="flex-1 truncate text-sm text-slate-800">{l.title}</span>
                  {l.is_preview && <Badge variant="secondary">Preview</Badge>}
                  {!l.required && <Badge variant="secondary">Optional</Badge>}
                  <IconBtn label="Edit lesson" onClick={() => setDialog({ kind: "lesson", lesson: l })} testid={`lesson-edit-${mi}-${li}`}><Pencil className="h-4 w-4" /></IconBtn>
                  <IconBtn label="Move up" onClick={() => run(() => api.post(`/admin/lessons/${l.id}/move`, { direction: "up" }))} testid={`lesson-up-${mi}-${li}`}><ArrowUp className="h-4 w-4" /></IconBtn>
                  <IconBtn label="Move down" onClick={() => run(() => api.post(`/admin/lessons/${l.id}/move`, { direction: "down" }))} testid={`lesson-down-${mi}-${li}`}><ArrowDown className="h-4 w-4" /></IconBtn>
                  <IconBtn label="Delete lesson" onClick={() => window.confirm(`Delete "${l.title}"?`) && run(() => api.delete(`/admin/lessons/${l.id}`), "Lesson deleted")} testid={`lesson-delete-${mi}-${li}`}><Trash2 className="h-4 w-4" /></IconBtn>
                </li>
              ))}
            </ul>
            <div className="p-3"><Button size="sm" variant="ghost" onClick={() => setDialog({ kind: "lesson", moduleId: m.id })} data-testid={`module-add-lesson-${mi}`}><Plus className="mr-1 h-4 w-4" /> Add lesson</Button></div>
          </div>
        ))}
        <div className="flex gap-2 rounded-2xl border border-dashed border-slate-300 bg-white p-4">
          <Input value={newModule} onChange={(e) => setNewModule(e.target.value)} placeholder="New module title" maxLength={140} data-testid="new-module-input" />
          <Button onClick={addModule} disabled={newModule.trim().length < 2} data-testid="add-module-btn"><Plus className="mr-1 h-4 w-4" /> Add module</Button>
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle>{dialog?.kind === "course" ? "Course details" : dialog?.lesson ? "Edit lesson" : "New lesson"}</DialogTitle></DialogHeader>
          {dialog?.kind === "course" && <CourseForm initial={c} onSubmit={saveCourse} />}
          {dialog?.kind === "lesson" && <LessonForm initial={dialog.lesson} onSubmit={saveLesson} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
